import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import Container from '../../Grid/Container'
import PropTypes from 'prop-types'
import Icon from '../../Icon'
import OutsideClickHandler from 'react-outside-click-handler'
import Spinner from '../../Spinner'
import Category from '../../Selectors/Category'
import HelperText from '../HelperText'
import styles from '../form-controls.module.scss'
import TextInput from '../TextInput'

const Select = ({
  variant = 'outlined',
  className = '',
  focus = false,
  fullWidth = false,
  hidden = false,
  disabled = false,
  onChange = () => {},
  onChangeOption = () => {},
  onFocus = () => {},
  onClickHandle = () => {},
  label,
  error,
  name,
  helperText = null,
  ControlProps = {
    apply: 'Apply',
    dropSelection: 'Drop selection',
  },
  value = null,
  onBlur = () => {},
  onBlurOption = () => {},
  //onKeyDown = () => {},
  readOnly = false,
  emptyOptionLabel = '--Select--',
  clearOptionLabel = null,
  noResultsLabel = 'No results',
  options = [],
  style = {},
  activeOptionLabelTemplate = (option) =>
    Array.isArray(option)
      ? option.map(({ label }) => label).join(', ')
      : option.label,

  optionLabelTemplate = ({ label }) => label,
  mode = 'default',
  getDataMethod = null,
  labelFieldName = 'label',
  showEmptyOption = true,
  groupName = null,
  multiple = false,
  size = 'l',
}) => {
  const [optionsLoading, setOptionsLoading] = useState(false)
  const dropdownContainer = useRef()
  const [isFocused, setFocused] = useState(focus)
  const inputRef = useRef(null)

  const emptyOption = useMemo(
    () => ({
      empty: true,
      label: !value
        ? emptyOptionLabel
        : clearOptionLabel
        ? clearOptionLabel
        : emptyOptionLabel,
    }),
    [emptyOptionLabel, value, clearOptionLabel],
  )

  const addEmpty = useCallback(
    (opts) => (showEmptyOption ? [emptyOption, ...opts] : opts),
    [showEmptyOption, emptyOption],
  )

  const [isEmpty, setEmpty] = useState(!value)
  const [currentOptions, setCurrentOptions] = useState(addEmpty(options))
  const [filteredOptions, setFilteredOptions] = useState(addEmpty(options))
  const [searchPhrase, setSearchPhrase] = useState('')
  const filterPagination = useRef()

  const plainOptions = useMemo(
    () =>
      groupName
        ? currentOptions.reduce((acc, group) => {
            if (group.items) {
              acc = acc.concat(group.items)
            } else {
              acc.push(group)
            }

            return acc
          }, [])
        : currentOptions,
    [currentOptions, groupName],
  )

  const selectText = useMemo(() => {
    if (multiple) {
      const activeOptions =
        plainOptions.filter((option) => value?.includes(option.value)) || []
      return activeOptions.length
        ? activeOptionLabelTemplate(activeOptions)
        : null
    } else {
      const activeOption = plainOptions.find((option) => option.value === value)
      return activeOption ? activeOptionLabelTemplate(activeOption) : null
    }
  }, [value, activeOptionLabelTemplate, plainOptions, multiple])

  useEffect(() => {
    filterPagination.current = { page: 1, total_pages: 1 }
  }, [])

  useEffect(() => {
    if (!getDataMethod) {
      setCurrentOptions(addEmpty(options))
      setFilteredOptions(addEmpty(options))
    }
  }, [options, addEmpty, getDataMethod, setCurrentOptions, setFilteredOptions])

  const handleChange = useCallback(
    (option) => {
      const { disabled, value, label, empty } = option

      setFocused(false)
      if (!disabled) {
        onChange(value || null)
        onChangeOption(option)

        setTimeout(() => {
          onBlur(value || null)
          onBlurOption(option)
        }, 125)
      }

      if (mode === 'filter') {
        if (empty) {
          setSearchPhrase('')
        } else {
          if (getDataMethod) {
            setCurrentOptions(filteredOptions)
          }
          setSearchPhrase(label)
        }
      }
    },
    [
      onChangeOption,
      onBlurOption,
      onChange,
      onBlur,
      setCurrentOptions,
      filteredOptions,
      setFocused,
      mode,
      getDataMethod,
      setSearchPhrase,
    ],
  )

  const multipleHandleChange = useCallback(
    (values) => {
      onChange(values)

      onBlur(values)
      setFocused(false)

      if (mode === 'filter') {
        if (getDataMethod) {
          setCurrentOptions(filteredOptions)
        }
      }
    },
    [
      mode,
      filteredOptions,
      onChange,
      onBlur,
      setFocused,
      setCurrentOptions,
      getDataMethod,
    ],
  )

  const multipleHandleReset = useCallback(() => {
    onChange(null)
    setSearchPhrase('')
    onBlur(null)
    setFocused(false)
  }, [onBlur, onChange])

  const getOptionsList = useCallback(
    (searchPhrase) => {
      filterPagination.current = { page: 1, total_pages: 1 }

      setFilteredOptions([])
      setOptionsLoading(true)
      getDataMethod({ searchPhrase, page: filterPagination.current.page })
        .then(({ items, pagination }) => {
          setFilteredOptions(addEmpty(items))
          filterPagination.current = pagination
        })
        .then(() => setOptionsLoading(false))
        .catch(() => {
          setFilteredOptions(addEmpty([]))
        })
    },
    [
      filterPagination,
      setOptionsLoading,
      getDataMethod,
      setFilteredOptions,
      addEmpty,
    ],
  )

  const getNextOptions = useCallback(() => {
    if (optionsLoading) {
      return false
    }

    setOptionsLoading(true)

    getDataMethod({ searchPhrase, page: filterPagination.current.page + 1 })
      .then(({ items, pagination }) => {
        setFilteredOptions((prev) => [...prev, ...items])
        filterPagination.current = pagination
      })
      .then(() => setOptionsLoading(false))
      .catch((err) => {
        console.info(err)
      })
  }, [
    getDataMethod,
    searchPhrase,
    optionsLoading,
    setOptionsLoading,
    filterPagination,
    setFilteredOptions,
  ])

  useEffect(() => {
    if (isFocused && mode === 'filter' && getDataMethod) {
      getOptionsList(searchPhrase)
    } else {
      setFilteredOptions(
        searchPhrase
          ? [
              ...currentOptions.filter((opt) =>
                opt[labelFieldName]
                  .toLowerCase()
                  .includes(searchPhrase.toLowerCase()),
              ),
            ]
          : currentOptions,
      )
    }
  }, [searchPhrase, currentOptions, isFocused, labelFieldName, mode])

  useEffect(() => {
    setEmpty(!value)
  }, [value])

  useEffect(() => {
    if (!isFocused && mode === 'filter') {
      setSearchPhrase('')
      if (!getDataMethod) {
        setCurrentOptions(addEmpty(options))
        setFilteredOptions(addEmpty(options))
      }
    }
  }, [isFocused, value, addEmpty, options])

  const handleClick = useCallback(
    (e) => {
      if (disabled || readOnly) {
        e.nativeEvent.stopImmediatePropagation()
        e.preventDefault()
        return
      }

      if (!(isFocused && e.target === inputRef.current && mode === 'filter')) {
        setFocused(!isFocused)
      }

      onClickHandle(e)
    },
    [disabled, readOnly, isFocused, mode, onClickHandle],
  )

  useEffect(() => {
    if (isFocused) {
      inputRef.current.focus()
    } else {
      inputRef.current.blur()
    }
  }, [isFocused])

  const handleFocus = useCallback(
    (e) => {
      if (disabled || readOnly) {
        return
      }

      onFocus(e.target.value)
    },
    [isFocused, value],
  )

  const startAdornment = ControlProps.startAdornment ? (
    <div
      className={clsx(
        styles['control__adornment'],
        styles['control__adornment_start'],
      )}
    >
      {ControlProps.startAdornment}
    </div>
  ) : null

  const endAdornment = (
    <>
      <div className={styles['control__triggerButtonContainer']}>
        <Icon
          name={'arrow1-down'}
          size={size === 's' ? 's' : 'm'}
          className={clsx(styles['control__triggerButton'], {
            [styles['control__triggerButton_disabled']]: disabled || readOnly,
          })}
        />
      </div>
      {ControlProps.endAdornment}
    </>
  )

  useEffect(() => {
    if (!getDataMethod) {
      return false
    }

    function handleScroll(e) {
      if (
        e.target.scrollTop + e.target.clientHeight >
        e.target.scrollHeight - 100
      ) {
        if (
          filterPagination.current.page !== filterPagination.current.total_page
        ) {
          getNextOptions()
        }
      }
    }

    if (dropdownContainer.current) {
      dropdownContainer.current.addEventListener('scroll', handleScroll, false)
    }

    return () => {
      if (dropdownContainer.current) {
        dropdownContainer.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const Component = useMemo(
    () => (
      <TextInput
        ref={inputRef}
        size={size}
        readOnly={mode !== 'filter' || readOnly}
        disabled={disabled}
        label={label}
        value={mode === 'filter' && isFocused ? searchPhrase : selectText || ''}
        onChange={({ currentTarget: { value } }) => setSearchPhrase(value)}
        onFocus={handleFocus}
        fullWidth
        autocomplete={'off'}
        InputProps={{
          startAdornment: startAdornment,
          endAdornment: endAdornment,
        }}
      />
    ),
    [mode, selectText, isFocused, startAdornment, endAdornment, size],
  )

  return (
    <div
      className={clsx(
        'control-component',
        styles['control-component'],
        styles[`control-component_variant_${variant}`],
        styles[`control-component_select`],
        {
          [styles['control-component_empty']]: isEmpty && !disabled,
          [styles['control-component_focused']]: isFocused,
          [styles['control-component_error']]: error,
          [styles['control-component_fullWidth']]: fullWidth,
          [styles['control-component_withHelperText']]: helperText,
          [styles['control-component_hasAdornment']]:
            Object.hasOwn(ControlProps, 'startAdornment') ||
            Object.hasOwn(ControlProps, 'endAdornment'),
          [styles['control-component_hasStartAdornment']]:
            Object.hasOwn(ControlProps, 'startAdornment') &&
            ControlProps.startAdornment,
          [styles['control-component_hasEndAdornment']]:
            Object.hasOwn(ControlProps, 'endAdornment') &&
            ControlProps.endAdornment,
          [styles['control-component_hasLabel']]: !!label,
          [styles['control-component_disabled']]: disabled,
          [styles['control-component_hidden']]: hidden,
          [styles['control-component_readonly']]: readOnly,
        },
        className,
      )}
      style={style}
    >
      <OutsideClickHandler
        disabled={!isFocused}
        useCapture={true}
        onOutsideClick={handleClick}
      >
        <div>
          <div onClick={handleClick}>{Component}</div>

          {
            <div
              className={clsx(styles['control__dropdown'], {
                [styles['control__dropdown_multiple']]: multiple,
                [styles['control__dropdown_visible']]: isFocused,
              })}
              ref={dropdownContainer}
            >
              {multiple ? (
                <Category
                  values={!value ? [] : value}
                  groups={
                    filteredOptions.length
                      ? filteredOptions.map(({ value = null, label }) => ({
                          id: value,
                          label,
                        }))
                      : [{ label: noResultsLabel }]
                  }
                  className={clsx(
                    styles['control__dropdown-list'],
                    styles['control__dropdown-list_multiple'],
                  )}
                  onReset={multipleHandleReset}
                  onSelect={multipleHandleChange}
                  i18n={{
                    dropSelection: ControlProps.dropSelection,
                    apply: ControlProps.apply,
                  }}
                />
              ) : (
                <ul className={styles['control__dropdown-list']}>
                  {filteredOptions?.length > 0 &&
                    filteredOptions.map((option, i) => {
                      return !option[groupName] ? (
                        <li
                          key={name + '-' + option.value}
                          className={clsx(
                            styles['control__dropdown-list-item'],
                            {
                              [styles['control__dropdown-list-item_empty']]:
                                option.empty,
                              [styles['control__dropdown-list-item_disabled']]:
                                option.disabled,
                            },
                          )}
                          onClick={() => handleChange(option)}
                        >
                          {option.empty
                            ? option.label
                            : optionLabelTemplate(option)}
                        </li>
                      ) : (
                        <li
                          key={name + '-group-' + i}
                          className={
                            styles['control__dropdown-list-group-item']
                          }
                        >
                          <div
                            className={
                              styles['control__dropdown-list-group-name']
                            }
                          >
                            {option.group}
                          </div>
                          <ul>
                            {option.items.map((option) => (
                              <li
                                key={name + '-' + option.value}
                                className={clsx(
                                  styles['control__dropdown-list-item'],
                                  {
                                    [styles[
                                      'control__dropdown-list-item_empty'
                                    ]]: option.empty,
                                    [styles[
                                      'control__dropdown-list-item_disabled'
                                    ]]: option.disabled,
                                  },
                                )}
                                onClick={() => handleChange(option)}
                              >
                                {option.empty
                                  ? option.label
                                  : optionLabelTemplate(option)}
                              </li>
                            ))}
                          </ul>
                        </li>
                      )
                    })}
                  {optionsLoading === false &&
                    filteredOptions?.length === 0 && (
                      <li
                        className={clsx(
                          styles['control__dropdown-list-item'],
                          styles['control__dropdown-list-item_empty'],
                        )}
                      >
                        {noResultsLabel}
                      </li>
                    )}
                  {optionsLoading === true && (
                    <Container
                      display={'flex'}
                      flexAlign={'center'}
                      flexJustify={'center'}
                      className={clsx(
                        styles['control__dropdown-list-item'],
                        styles['control__dropdown-list-item_disabled'],
                      )}
                    >
                      <Spinner width={20} />
                    </Container>
                  )}
                </ul>
              )}
            </div>
          }
        </div>
      </OutsideClickHandler>

      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </div>
  )
}

Select.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    PropTypes.oneOf([null]),
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        PropTypes.oneOf([null]),
      ]),
    ),
  ]),
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  fullWidth: PropTypes.bool,
  helperText: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.node,
  ]),
  hidden: PropTypes.bool,
  ControlProps: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  variant: PropTypes.oneOf(['standard', 'outlined']),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      disabled: PropTypes.bool,
    }),
  ),
  style: PropTypes.object,
  tabIndex: PropTypes.number,
  readOnly: PropTypes.bool,
  onClickHandle: PropTypes.func,
  onBlur: PropTypes.func,
  onFocusHandle: PropTypes.func,
  name: PropTypes.string,
  focus: PropTypes.bool,
  className: PropTypes.string,
  activeOptionLabelTemplate: PropTypes.func,
  mode: PropTypes.oneOf(['default', 'filter']),
  labelFieldName: PropTypes.string,
  multiple: PropTypes.bool,
  optionsLoading: PropTypes.bool,
  noResultsLabel: PropTypes.string,
  onChangeOption: PropTypes.func,
  onFocus: PropTypes.func,
  onBlurOption: PropTypes.func,
  emptyOptionLabel: PropTypes.string,
  clearOptionLabel: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.string,
  ]),
  optionLabelTemplate: PropTypes.func,
  getDataMethod: PropTypes.func,
  showEmptyOption: PropTypes.bool,
  groupName: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.string]),
  size: PropTypes.oneOf(['s', 'm', 'l']),
}

export default Select
